import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const colorMap: Record<string, string> = {
  "custom.blue4": "#8697C4",
  "custom.blue6": "#5A6AA5",
  "custom.blue3": "#ADBBDA",
  "custom.norisk": "#A4CF87",
  "custom.riskverylow": "#FFDA65",
  "custom.risklow": "#FEDEDE",
  "custom.riskmoderate": "#FDB5B5",
  "custom.riskhigh": "#FB8585",
  "custom.riskveryhigh": "#F84242",
  "custom.riskcritical": "#E20808",
};

type LineChartData = {
  name: string;
  noOfItems: number;
  color: string;
  value: string;
};

type CRQCDashboardProps = {
  absoluteYearsData: LineChartData[];
  title: string;
  footer: string;
};

const LineChartComponent: React.FC<CRQCDashboardProps> = ({
  absoluteYearsData,
  title,
  footer,
}) => {
  const renderLineChart = (data: LineChartData[]) => (
    <div
      className="flex flex-col items-start w-full p-2"
      style={{ height: "250px" }}
    >
      {/* Title */}
      {title && (
        <>
          <h3 className="font-bold text-custom-blue7 mb-2">{title}</h3>
          <div className="w-full h-0.5 bg-gray-300 mb-4"></div>
        </>
      )}
      {/* Line Chart */}
      <ResponsiveContainer>
        <LineChart
          data={data}
          margin={{ top: 0, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            label={{ value: "", position: "insideBottom", dy: 10 }}
          />
          <YAxis
            label={{
              value: "",
              angle: -90,
              position: "insideLeft",
              dy: -5,
              dx: 20,
            }}
          />
          <Tooltip
            // labelFormatter={(label: string) => `Shelf Life: ${label} yrs`} // Adding year (x-axis value) to the tooltip
            labelFormatter={(currentLabel: string, payload: any[]) => {
              if (!payload || payload.length === 0) {
                return `${currentLabel}`; // Fallback if no payload
              }

              // Find the current index based on the payload
              const currentIndex = data.findIndex(
                (item) => item.name === currentLabel
              );
              const prevLabel =
                currentIndex > 0 ? data[currentIndex - 1].name : "Start";

              return `${currentLabel}`;
            }}
            formatter={(value: number, name: string) => [
              `${value}%`,
              "Percentage",
            ]} // Customizing tooltip
          />
          <Line
            type="linear"
            dataKey="noOfItems"
            stroke="#8884d8"
            strokeWidth={2}
            activeDot={{ r: 8 }}
          />
        </LineChart>
      </ResponsiveContainer>
      {/* Footer */}
      <p className="text-custom-blue6">{footer}</p>
    </div>
  );

  return (
    <div className="mx-auto p-6 bg-white rounded-md shadow-md">
      {/* Line Chart */}
      <div className="flex flex-wrap justify-center">
        {absoluteYearsData.length > 0 ? (
          renderLineChart(absoluteYearsData)
        ) : (
          <div className="h-[240px] flex items-center justify-center">
            <h1 className="text-custom-blue6 text-xl">Data Not Found!</h1>
          </div>
        )}
      </div>
    </div>
  );
};

export default LineChartComponent;
